import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Stack, HStack, Button, useToast } from "@chakra-ui/react";
import {
  getSuggestionDataDataAPI,
  getResearchCategoriesAPI,
} from "../../services/Films";
import { fetchMasterlistSubTypes } from "../../util/getMasterlistSubType";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import TypeaheadWidget from "../../CommonComponents/TypeaheadWidget";
import ImageWidget from "../../CommonComponents/ImageWidget";
import { getDeletedValues, removeKeysFromFormData } from "../../util/helper";
import {
  getLocationData,
  updateLocationBasicData,
  getLocationAliasSuggestions,
} from "../../services/Location";
import FormLoader from "../../CommonComponents/FormLoader";
import {
  getApiErrorMessage,
  showErrorToast,
  showSuccessToast,
} from "../../util/errorHandler";
import {
  homeCollageOrderSchema,
  homeCollageOrderUiSchema,
} from "../../constants/homeCollage";
import { parseHomeCollageConflict } from "../../util/homeCollageConflict";
import { useHomeCollageReassign } from "../../hooks/useHomeCollageReassign";
import HomeCollageReassignDialog from "../../CommonComponents/HomeCollageReassignDialog";
import {
  exploreCarouselOrderSchema,
  exploreCarouselOrderUiSchema,
} from "../../constants/exploreCarousel";
import {
  exploreSearchHintOrderSchema,
  exploreSearchHintOrderUiSchema,
  exploreSearchHintKindSchema,
  exploreSearchHintKindUiSchema,
  useSearchHintPoolSize,
} from "../../constants/exploreSearchHints";
/**
 * Trims each string in the array and removes empty/whitespace-only values.
 * Always returns an array (never null/undefined) so the API always receives
 * a clean array payload — even when the user entered nothing.
 *
 * Scale note: operates in O(n) time; safe for any realistic AKA list size.
 *
 * @param {Array<string>} arr - Raw array from form state
 * @returns {Array<string>} Cleaned array with no empty strings
 */
const cleanArray = (arr) =>
  (arr ?? []).map((v) => (v ?? "").trim()).filter((v) => v !== "");

// Left Column Schema - Basic Fields
const getLeftColumnSchema = (hasIsExplore) => {
  const baseProperties = {
    name: {
      type: "string",
      title: "Name",
    },
    alias: {
      type: "string",
      title: "Alias",
    },
    meta_title: {
      type: "string",
      title: "Meta Title",
    },
    meta_description: {
      type: "string",
      title: "Meta Description",
    },
    subject: {
      type: "string",
      title: "Masterlist Type",
    },
    masterlist_type: {
      type: "string",
      title: "Masterlist Sub Type",
    },
    image_alias: {
      type: ["string", "null"],
      title: "Image",
    },
    image_alias_preview: {
      type: ["string", "null"],
      title: "Image Preview",
    },
    researchCategories: {
      type: "array",
      title: "Research Category",
      items: {
        type: "object",
        properties: {
          alias: {
            type: "string",
            title: "Alias",
          },
        },
      },
    },
  };

  return {
    title: "Primary fields",
    description: "",
    type: "object",
    properties: baseProperties,
    required: ["masterlist_type", "subject"],
  };
};

// Right Column Schema - Metadata Fields
const getRightColumnSchema = (hasIsExplore) => {
  const baseProperties = {
    linked_cities: {
      type: "array",
      title: "Linked Cities",
      items: {
        type: "object",
        properties: {
          alias: {
            type: "string",
            title: "Alias",
          },
        },
      },
    },
    is_capital_city: {
      type: "string",
      title: "Is Capital City ?",
      enum: ["Yes", "No"],
    },
    // AKA: optional dynamic array of strings (PRD §1, §4)
    aka: {
      type: ["array", "null"],
      title: "AKA",
      items: {
        type: "string",
      },
    },
    // AKA Type Tolerance: optional dynamic array of strings (PRD §1, §4)
    aka_typo_tolerance: {
      type: ["array", "null"],
      title: "AKA Typo Tolerance",
      items: {
        type: "string",
      },
    },
    isExplore: {
      type: "boolean",
      title: "Is Explore",
      default: false,
    },
  };

  // Add isExplore fields only if isExplore is true
  if (hasIsExplore) {
    baseProperties.display_name = {
      type: "string",
      title: "Display Name",
    };
    baseProperties.sort_name = {
      type: "string",
      title: "Sort Name",
    };
    baseProperties.route_type = {
      type: "string",
      title: "Route",
    };
    baseProperties.image = {
      type: "string",
      title: "Explore Image URL",
    };
    baseProperties.home_collage_order = homeCollageOrderSchema();
    baseProperties.explore_carousel_order = exploreCarouselOrderSchema();
    baseProperties.explore_search_hint_order = exploreSearchHintOrderSchema();
    baseProperties.explore_search_hint_kind = exploreSearchHintKindSchema();
  }

  const requiredFields = [];
  if (hasIsExplore) {
    requiredFields.push("route_type");
    requiredFields.push("image");
  }

  return {
    title: "",
    description: "",
    type: "object",
    properties: baseProperties,
    required: requiredFields,
  };
};

// Combined Schema for Single Column (Preview Mode)
const getCombinedSchema = (hasIsExplore) => {
  const baseProperties = {
    name: {
      type: "string",
      title: "Name",
    },
    alias: {
      type: "string",
      title: "Alias",
    },
    meta_title: {
      type: "string",
      title: "Meta Title",
    },
    meta_description: {
      type: "string",
      title: "Meta Description",
    },
    subject: {
      type: "string",
      title: "Masterlist Type",
    },
    masterlist_type: {
      type: "string",
      title: "Masterlist Sub Type",
    },
    image_alias: {
      type: ["string", "null"],
      title: "Image",
    },
    image_alias_preview: {
      type: ["string", "null"],
      title: "Image Preview",
    },
    researchCategories: {
      type: "array",
      title: "Research Category",
      items: {
        type: "object",
        properties: {
          alias: {
            type: "string",
            title: "Alias",
          },
        },
      },
    },
    linked_cities: {
      type: "array",
      title: "Linked Cities",
      items: {
        type: "object",
        properties: {
          alias: {
            type: "string",
            title: "Alias",
          },
        },
      },
    },
    is_capital_city: {
      type: "string",
      title: "Is Capital City ?",
      enum: ["Yes", "No"],
    },
    // AKA: optional dynamic array of strings (PRD §1, §4)
    aka: {
      type: ["array", "null"],
      title: "AKA",
      items: {
        type: "string",
      },
    },
    // AKA Type Tolerance: optional dynamic array of strings (PRD §1, §4)
    aka_typo_tolerance: {
      type: ["array", "null"],
      title: "AKA Typo Tolerance",
      items: {
        type: "string",
      },
    },
    isExplore: {
      type: "boolean",
      title: "Is Explore",
      default: false,
    },
  };

  // Add isExplore fields only if isExplore is true
  if (hasIsExplore) {
    baseProperties.display_name = {
      type: "string",
      title: "Display Name",
    };
    baseProperties.sort_name = {
      type: "string",
      title: "Sort Name",
    };
    baseProperties.route_type = {
      type: "string",
      title: "Route",
    };
    baseProperties.image = {
      type: "string",
      title: "Explore Image URL",
    };
    baseProperties.home_collage_order = homeCollageOrderSchema();
    baseProperties.explore_carousel_order = exploreCarouselOrderSchema();
    baseProperties.explore_search_hint_order = exploreSearchHintOrderSchema();
    baseProperties.explore_search_hint_kind = exploreSearchHintKindSchema();
  }

  const requiredFields = ["masterlist_type", "subject"];
  if (hasIsExplore) {
    requiredFields.push("route_type");
  }

  return {
    title: "Primary fields",
    description: "",
    type: "object",
    properties: baseProperties,
    required: requiredFields,
  };
};

// Left Column UI Schema
const getLeftColumnUiSchema = (hasIsExplore) => (isSubmitting) => {
  const baseSchema = {
    "ui:submitButtonOptions": {
      norender: true,
    },
    "ui:style": {
      fontSize: "14px",
    },
    typeaheadField: {
      "ui:widget": "typeahead",
    },
    researchCategories: {
      items: {
        alias: {
          "ui:widget": "typeahead1",
        },
      },
    },
    alias: { "ui:readonly": true },
    meta_title: { "ui:emptyValue": "" },
    meta_description: { "ui:widget": "textarea", "ui:emptyValue": "" },
    name: { "ui:emptyValue": "" },
    subject: { "ui:emptyValue": "" },
    masterlist_type: { "ui:widget": "typeahead3", "ui:emptyValue": "" },
    image_alias_preview: {
      "ui:widget": ImageWidget,
      "ui:options": {
        "ui:emptyValue": null,
      },
    },
    image_alias: { "ui:emptyValue": "" },
  };

  // Add image field only if isExplore is true
  if (hasIsExplore) {
    baseSchema.image = { "ui:emptyValue": "" };
  }

  return baseSchema;
};

// Right Column UI Schema
const getRightColumnUiSchema = (hasIsExplore) => (isSubmitting) => {
  const baseSchema = {
    "ui:submitButtonOptions": {
      norender: true,
    },
    "ui:style": {
      fontSize: "14px",
    },
    linked_cities: {
      items: {
        alias: {
          "ui:widget": "typeahead2",
        },
      },
    },
    is_capital_city: {
      "ui:widget": "radio",
      "ui:emptyValue": "",
      "ui:options": { inline: true },
      props: {
        display: "flex",
        flexDirection: "row",
        gap: "10px",
      },
    },
    // AKA: render as dynamic list of text inputs (PRD §3)
    aka: { "ui:emptyValue": "" },
    // AKA Type Tolerance: render as dynamic list of text inputs (PRD §3)
    aka_typo_tolerance: { "ui:emptyValue": "" },
    isExplore: { "ui:emptyValue": false },
  };

  // Add isExplore fields only if isExplore is true
  if (hasIsExplore) {
    baseSchema.display_name = { "ui:emptyValue": "" };
    baseSchema.sort_name = { "ui:emptyValue": "" };
    baseSchema.route_type = { "ui:emptyValue": "" };
    baseSchema.image = { "ui:emptyValue": "" };
    baseSchema.home_collage_order = homeCollageOrderUiSchema();
    baseSchema.explore_carousel_order = exploreCarouselOrderUiSchema();
    baseSchema.explore_search_hint_order = exploreSearchHintOrderUiSchema();
    baseSchema.explore_search_hint_kind = exploreSearchHintKindUiSchema();
  }

  return baseSchema;
};

// Combined UI Schema for Single Column (Preview Mode)
const getCombinedUiSchema = (hasIsExplore) => (isSubmitting) => {
  const baseSchema = {
    "ui:submitButtonOptions": {
      norender: true,
    },
    "ui:style": {
      fontSize: "14px",
    },
    typeaheadField: {
      "ui:widget": "typeahead",
    },
    researchCategories: {
      items: {
        alias: {
          "ui:widget": "typeahead1",
        },
      },
    },
    linked_cities: {
      items: {
        alias: {
          "ui:widget": "typeahead2",
        },
      },
    },
    alias: { "ui:readonly": true },
    meta_title: { "ui:emptyValue": "" },
    meta_description: { "ui:widget": "textarea", "ui:emptyValue": "" },
    name: { "ui:emptyValue": "" },
    subject: { "ui:emptyValue": "" },
    masterlist_type: { "ui:widget": "typeahead3", "ui:emptyValue": "" },
    image_alias_preview: {
      "ui:widget": ImageWidget,
      "ui:options": {
        "ui:emptyValue": null,
      },
    },
    image_alias: { "ui:emptyValue": "" },
    is_capital_city: {
      "ui:widget": "radio",
      "ui:emptyValue": "",
      "ui:options": { inline: true },
      props: {
        display: "flex",
        flexDirection: "row",
        gap: "10px",
      },
    },
    // AKA: render as dynamic list of text inputs (PRD §3)
    aka: { "ui:emptyValue": "" },
    // AKA Type Tolerance: render as dynamic list of text inputs (PRD §3)
    aka_typo_tolerance: { "ui:emptyValue": "" },
    isExplore: { "ui:emptyValue": false },
  };

  // Add isExplore fields only if isExplore is true
  if (hasIsExplore) {
    baseSchema.display_name = { "ui:emptyValue": "" };
    baseSchema.sort_name = { "ui:emptyValue": "" };
    baseSchema.route_type = { "ui:emptyValue": "" };
    baseSchema.image = { "ui:emptyValue": "" };
    baseSchema.home_collage_order = homeCollageOrderUiSchema();
    baseSchema.explore_carousel_order = exploreCarouselOrderUiSchema();
    baseSchema.explore_search_hint_order = exploreSearchHintOrderUiSchema();
    baseSchema.explore_search_hint_kind = exploreSearchHintKindUiSchema();
  }

  return baseSchema;
};

const Basic = () => {
  const { alias } = useParams();
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  // Original data snapshot used for computing deleted values on submit
  const [basicFieldData, setBasicFieldData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const homeCollageReassign = useHomeCollageReassign();
  const toast = useToast();
  // Re-renders once the live hint pool size (app_configs) is known.
  useSearchHintPoolSize();
  const previewEnabled = useSelector((state) => state.preview.isEnabled);
  const leftFormRef = useRef(null);
  const rightFormRef = useRef(null);
  const combinedFormRef = useRef(null);

  const fetchOptions = async (inputValue) => {
    const result = await getSuggestionDataDataAPI(inputValue);

    return result?.data?.map((item) => ({
      label: item.title,
      value: item.title,
    }));
  };

  // Function for create options
  const transformOptions = (data) => {
    return data?.map((itemArray) => {
      // Convert the array of key-value pairs into a single object
      const entity = itemArray?.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {});

      // Use the `alias` field for label and value
      return {
        label: entity.alias,
        value: entity.alias,
      };
    });
  };

  const fetchCategories = async (inputValue) => {
    const result = await getResearchCategoriesAPI();
    const transformedOptions = transformOptions(result);

    // This function return the options
    return transformedOptions?.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  const fetchLocationSuggestions = async (inputValue) => {
    try {
      const result = await getLocationAliasSuggestions(20, 1, inputValue);
      const locationData = result?.data?.data || [];
      return locationData.map((item) => ({
        label: item.alias || item.name || "",
        value: item.alias || item.name || "",
      }));
    } catch (error) {
      console.error("Error fetching location suggestions:", error);
      return [];
    }
  };

  const transFormData = (data) => ({
    ...data,
    meta_title: data?.meta_title ?? "",
    meta_description: data?.meta_description ?? "",
    image_alias_preview: data?.image_alias || null,
    isExplore: data?.isExplore ?? false,
    // Default to [] when no value exists — no input rows rendered on load
    aka: data?.aka ?? [],
    aka_typo_tolerance: data?.aka_type_tolerance ?? [],
  });

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "name",
      "alias",
      "meta_title",
      "meta_description",
      "subject",
      "masterlist_type",
      "image_alias",
      "image_alias_preview",
      "researchCategories",
    ];
    // aka and aka_typo_tolerance live in the right column alongside other metadata (PRD §1)
    const rightFields = [
      "linked_cities",
      "is_capital_city",
      "aka",
      "aka_typo_tolerance",
      "display_name",
      "sort_name",
      "route_type",
      "isExplore",
      "image",
      "home_collage_order",
      "explore_carousel_order",
      "explore_search_hint_order",
      "explore_search_hint_kind",
    ];

    const leftData = {};
    const rightData = {};

    // Process left fields with defaults
    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        // Handle null values for image_alias (allowed by schema)
        if (
          field === "image_alias" &&
          (data[field] === null || data[field] === undefined)
        ) {
          leftData[field] = null;
        } else {
          leftData[field] = data[field];
        }
      } else {
        if (field === "researchCategories") {
          leftData[field] = [];
        } else if (field === "image_alias_preview") {
          leftData[field] = null;
        } else if (field === "image_alias") {
          leftData[field] = null; // Allow null for optional image_alias
        } else {
          leftData[field] = "";
        }
      }
    });

    // Process right fields with defaults
    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      } else {
        if (field === "linked_cities") {
          rightData[field] = [];
        } else if (field === "isExplore") {
          rightData[field] = false;
        } else if (field === "aka" || field === "aka_typo_tolerance") {
          // Default to [] when no value exists — no input rows rendered on load
          rightData[field] = [];
        } else if (
          field === "home_collage_order" ||
          field === "explore_carousel_order" ||
          field === "explore_search_hint_order"
        ) {
          rightData[field] = null;
        } else {
          rightData[field] = "";
        }
      }
    });

    return { leftData, rightData };
  };

  // Combine data from both forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Check if isExplore is true (not just if it exists)
  const hasIsExplore = useMemo(() => {
    const dataToCheck = previewEnabled ? combinedFormData : rightFormData;
    return dataToCheck?.isExplore === true;
  }, [combinedFormData, rightFormData, previewEnabled]);

  const leftColumnSchema = useMemo(
    () => getLeftColumnSchema(hasIsExplore),
    [hasIsExplore],
  );
  const rightColumnSchema = useMemo(
    () => getRightColumnSchema(hasIsExplore),
    [hasIsExplore],
  );
  const combinedSchema = useMemo(
    () => getCombinedSchema(hasIsExplore),
    [hasIsExplore],
  );
  const leftColumnUiSchema = useMemo(
    () => getLeftColumnUiSchema(hasIsExplore),
    [hasIsExplore],
  );
  const rightColumnUiSchema = useMemo(
    () => getRightColumnUiSchema(hasIsExplore),
    [hasIsExplore],
  );
  const combinedUiSchema = useMemo(
    () => getCombinedUiSchema(hasIsExplore),
    [hasIsExplore],
  );

  const widgets = {
    typeahead: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchOptions} />
    ),
    typeahead1: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchCategories} />
    ),
    typeahead2: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchLocationSuggestions} />
    ),
    typeahead3: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchMasterlistSubTypes} />
    ),
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getLocationData(alias, "general", "basic");
          if (result) {
            const transformedData = transFormData(result?.data?.[0]);
            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(transformedData);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(transformedData);
            const keysToRemove = ["ssb_image", "image_alias_preview"];
            setBasicFieldData(
              removeKeysFromFormData(transformedData, keysToRemove),
            );
            setQuickSave(false);
          }
        } else {
          // For new records, initialize empty form data
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        showErrorToast(toast, "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
  }, [alias, toast, quickSave]);

  // Handle form changes for two-column layout
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Submit updated data
  const handleSubmit = async () => {
    // Use combined form data in preview mode, otherwise combine left and right forms
    const finalFormData = previewEnabled
      ? combinedFormData
      : combineFormData(leftFormData, rightFormData);

    // Validate required fields - RJSF should catch this, but this is a safeguard
    // Only validate route_type and image if isExplore is true
    if (hasIsExplore) {
      if (
        !finalFormData.route_type ||
        (typeof finalFormData.route_type === "string" &&
          finalFormData.route_type.trim() === "")
      ) {
        // Don't submit if route_type is empty when isExplore is true - RJSF validation will show the error
        return;
      }
      if (
        !finalFormData.image ||
        (typeof finalFormData.image === "string" &&
          finalFormData.image.trim() === "")
      ) {
        // Don't submit if image is empty when isExplore is true - RJSF validation will show the error
        return;
      }
    }

    // Clean AKA arrays before submission:
    // - Trim leading/trailing whitespace from each entry
    // - Remove empty/whitespace-only values
    // - Always send an array (even if empty) — never send empty strings (PRD §6)
    const cleanedFormData = {
      ...finalFormData,
      aka: cleanArray(finalFormData.aka),
      // Map form-state key back to API key before submission
      aka_type_tolerance: cleanArray(finalFormData.aka_typo_tolerance),
    };

    // Remove UI-only and form-state-only keys before sending to API
    const keysToRemove = [
      "ssb_image",
      "image_alias_preview",
      "aka_typo_tolerance",
    ];

    // Remove extra fields added which are not available in DB
    const updatedFormData = removeKeysFromFormData(
      cleanedFormData,
      keysToRemove,
    );
    const deletedValues = getDeletedValues(basicFieldData, updatedFormData);

    // Shared by the initial attempt and the confirm-reassign retry below --
    // extraPayload merges in confirm_home_collage_order_reassign: true only
    // when the admin has explicitly confirmed taking a taken slot (#266343).
    const submitLocation = (extraPayload = {}) =>
      updateLocationBasicData(
        alias,
        { ...updatedFormData, ...extraPayload },
        deletedValues,
      );

    try {
      if (alias) {
        setIsSubmitting(true);
        const result = await submitLocation();
        if (result?.success) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      const { conflict } = parseHomeCollageConflict(error);
      if (conflict) {
        // Let the admin confirm taking the slot instead of a dead-end error.
        // Confirming calls this same retry, with the flag set, so the slot
        // swap happens atomically on the backend in one call (#266343).
        homeCollageReassign.open(conflict, async () => {
          try {
            const result = await submitLocation({
              confirm_home_collage_order_reassign: true,
            });
            if (result?.success) {
              setQuickSave((prev) => !prev);
              showSuccessToast(toast, "Data updated successfully");
            }
          } catch (retryError) {
            showErrorToast(
              toast,
              getApiErrorMessage(retryError, "Failed to update data"),
            );
            throw retryError;
          }
        });
      } else {
        console.error("Error updating data:", error);
        showErrorToast(
          toast,
          getApiErrorMessage(error, "Failed to update data"),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <Box fontSize="lg" fontWeight="bold" mb={4}>
                  Primary fields
                </Box>
                <Form
                  ref={combinedFormRef}
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema(isSubmitting)}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  onSubmit={(e) => {
                    // RJSF validates before calling onSubmit
                    // e contains { formData, errors } - only proceed if no errors
                    if (!e.errors || e.errors.length === 0) {
                      handleSubmit();
                    }
                  }}
                  widgets={widgets}
                  fontsize={"14px"}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Box fontSize="lg" fontWeight="bold" mb={4}>
                      Primary fields
                    </Box>
                    <Form
                      ref={leftFormRef}
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema(isSubmitting)}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      onSubmit={(e) => {
                        // Prevent submission from left form - only right form triggers submission
                        // RJSF onSubmit receives { formData, errors }, not a DOM event
                      }}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      ref={rightFormRef}
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema(isSubmitting)}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      onSubmit={(e) => {
                        // RJSF validates before calling onSubmit
                        // e contains { formData, errors } - only proceed if no errors
                        if (!e.errors || e.errors.length === 0) {
                          handleSubmit();
                        }
                      }}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button at Bottom */}
      <Box
        position="fixed"
        bottom={0}
        left="250px" // Account for sidebar width
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
        display="flex"
      >
        <Box maxW="auto" display="flex" mx="auto">
          <Button
            colorScheme="blue"
            size="lg"
            width="auto"
            isLoading={isSubmitting}
            loadingText="Saving..."
            onClick={() => {
              // Trigger form validation by programmatically submitting all forms
              // This will trigger RJSF validation and show errors for required fields
              // (masterlist_type, subject, route_type if isExplore)
              const forms = document.querySelectorAll("form");

              if (forms.length > 0) {
                // In preview mode: validate the combined form (has all required fields)
                // In two-column mode: validate both left form (masterlist_type, subject)
                // and right form (route_type)
                forms.forEach((form) => {
                  // Create a temporary submit button to trigger validation
                  const tempSubmitBtn = document.createElement("button");
                  tempSubmitBtn.type = "submit";
                  tempSubmitBtn.style.display = "none";
                  form.appendChild(tempSubmitBtn);

                  // Trigger form submit which will validate and show errors
                  // The onSubmit handlers will call handleSubmit() only if validation passes
                  tempSubmitBtn.click();
                  form.removeChild(tempSubmitBtn);
                });
              } else {
                // Fallback: call handleSubmit directly if no forms found
                handleSubmit();
              }
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>

      {/* Add padding to prevent content from being hidden behind fixed button */}
      <Box pb="100px" />

      <HomeCollageReassignDialog
        isOpen={homeCollageReassign.isOpen}
        conflict={homeCollageReassign.conflict}
        isConfirming={homeCollageReassign.isConfirming}
        cancelRef={homeCollageReassign.cancelRef}
        onConfirm={homeCollageReassign.confirm}
        onCancel={homeCollageReassign.close}
      />
    </Box>
  );
};

export default Basic;
